import React, { useState } from 'react'
import { Box, Stack, Typography, Button, Menu, MenuItem } from '@mui/material'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { FaHome, FaBriefcase, FaBlog, FaUserAlt, FaEnvelope } from 'react-icons/fa'

import logo from '../Images/lyorauralogo.png'

const navContainerVariants = {
  hidden: { opacity: 0, y: -30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: 'easeOut', staggerChildren: 0.08, delayChildren: 0.2 },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: -10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
}

const mobileMenuVariants = {
  hidden: { opacity: 0, height: 0 },
  visible: {
    opacity: 1,
    height: 'auto',
    transition: { duration: 0.4, ease: 'easeInOut', staggerChildren: 0.06, delayChildren: 0.1 },
  },
  exit: { opacity: 0, height: 0, transition: { duration: 0.3, ease: 'easeInOut' } },
}

const mobileItemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, x: -20 },
}

const languages = [
  { code: 'ar', label: 'العربية', flag: '🇦🇪' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
]

const LanguageSelect = ({ i18n, fullWidth = false }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const currentLang = languages.find((l) => l.code === (i18n.language || 'ar').slice(0, 2)) || languages[0]

  const handleSelect = (code) => {
    i18n.changeLanguage(code)
    setAnchorEl(null)
  }

  return (
    <>
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
        fullWidth={fullWidth}
        sx={{
          color: '#D9D6CF',
          fontSize: '0.85rem',
          gap: 0.5,
          px: 1.5,
          py: 0.6,
          borderRadius: '999px',
          border: '1px solid rgba(217,214,207,0.2)',
          justifyContent: fullWidth ? 'space-between' : 'center',
          '&:hover': { bgcolor: 'rgba(217,214,207,0.08)', borderColor: 'rgba(217,214,207,0.35)' },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <span style={{ fontSize: '1.1rem' }}>{currentLang.flag}</span>
          <Typography component="span" sx={{ fontSize: '0.85rem' }}>
            {currentLang.code.toUpperCase()}
          </Typography>
        </Stack>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            mt: 1,
            bgcolor: 'rgba(43,43,43,0.9)',
            backdropFilter: 'blur(16px) saturate(160%)',
            border: '1px solid rgba(217,214,207,0.15)',
            borderRadius: '16px',
            minWidth: 150,
          },
        }}
      >
        {languages.map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => handleSelect(lang.code)}
            selected={lang.code === currentLang.code}
            sx={{
              gap: 1.2,
              color: '#D9D6CF',
              fontSize: '0.9rem',
              '&.Mui-selected': { bgcolor: 'rgba(122,46,58,0.35)' },
              '&.Mui-selected:hover': { bgcolor: 'rgba(122,46,58,0.45)' },
              '&:hover': { bgcolor: 'rgba(217,214,207,0.08)' },
            }}
          >
            <span style={{ fontSize: '1.1rem' }}>{lang.flag}</span>
            {lang.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

// خريطة الروابط لمساراتها - عدّل هنا لو غيّرت أي مسار
const navRoutes = {
  home: '/',
  projects: '/projects',
  Blogs: '/blogs',
  about: '/about',
  contact: '/contact',
}

export const Navebarehero = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const isArabic = i18n.language.startsWith('ar')

  const navLinks = [
    { key: 'home', label: t('nav.home'), icon: FaHome },
    { key: 'projects', label: t('nav.projects'), icon: FaBriefcase },
    { key: 'Blogs', label: t('nav.Blogs'), icon: FaBlog },
    { key: 'about', label: t('nav.about'), icon: FaUserAlt },
    { key: 'contact', label: t('nav.contact'), icon: FaEnvelope },
  ]

  const handleNavClick = (key) => {
    const path = navRoutes[key]
    if (path) navigate(path)
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: { xs: 16, md: 28 },
        left: { xs: 16, md: 32 },
        right: { xs: 16, md: 32 },
        zIndex: 1300,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 1100 }}>
        <Box
          component={motion.nav}
          variants={navContainerVariants}
          initial="hidden"
          animate="visible"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 1, md: 1.5 },
            borderRadius: '999px',
            bgcolor: 'rgba(43,43,43,0.45)',
            border: '1px solid rgba(217,214,207,0.15)',
            backdropFilter: 'blur(16px) saturate(160%)',
            WebkitBackdropFilter: 'blur(16px) saturate(160%)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
            overflow: 'visible',
          }}
        >
          {/* اللوجو - أكبر من ارتفاع الناف بار وبيخرج شوية من فوق وتحت */}
          <Box
            component={motion.div}
            variants={itemVariants}
            onClick={() => navigate('/')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              height: 0,
              cursor: 'pointer',
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="Lyor Aura"
              sx={{
                height: { xs: 90, sm: 100, md: 115 },
                width: 'auto',
                objectFit: 'contain',
                display: 'block',
                transform: 'translateY(-2px)',
                filter: 'brightness(0) invert(1)',
              }}
            />
          </Box>

          <Stack direction="row" spacing={3} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}>
            {navLinks.map(({ key, label, icon: Icon }) => (
              <Box component={motion.div} variants={itemVariants} key={key}>
                <Button
                  startIcon={<Icon size={16} />}
                  onClick={() => handleNavClick(key)}
                  sx={{
                    color: 'rgba(217,214,207,0.75)',
                    fontSize: '0.9rem',
                    gap: 0.5,
                    '&:hover': { color: '#D9D6CF', bgcolor: 'transparent' },
                  }}
                >
                  {label}
                </Button>
              </Box>
            ))}
            <Box component={motion.div} variants={itemVariants}>
              <LanguageSelect i18n={i18n} />
            </Box>
            <Box component={motion.div} variants={itemVariants}>
              <Button
                variant="contained"
                onClick={() => handleNavClick('contact')}
                sx={{
                  bgcolor: '#7A2E3A',
                  color: '#D9D6CF',
                  px: 3,
                  py: 1,
                  borderRadius: '999px',
                  '&:hover': { bgcolor: '#8f3646' },
                }}
              >
                {t('nav.cta')}
              </Button>
            </Box>
          </Stack>

          <Box
            component={motion.div}
            variants={itemVariants}
            onClick={() => setMenuOpen((prev) => !prev)}
            sx={{
              display: { xs: 'flex', md: 'none' },
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              width: 32,
              height: 32,
              cursor: 'pointer',
              gap: '6px',
            }}
          >
            <Box
              component={motion.span}
              animate={{ rotate: menuOpen ? 45 : 0, y: menuOpen ? 8 : 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              sx={{ width: 24, height: 2, bgcolor: '#D9D6CF', borderRadius: 2, display: 'block' }}
            />
            <Box
              component={motion.span}
              animate={{ opacity: menuOpen ? 0 : 1 }}
              transition={{ duration: 0.2 }}
              sx={{ width: 24, height: 2, bgcolor: '#D9D6CF', borderRadius: 2, display: 'block' }}
            />
            <Box
              component={motion.span}
              animate={{ rotate: menuOpen ? -45 : 0, y: menuOpen ? -8 : 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              sx={{ width: 24, height: 2, bgcolor: '#D9D6CF', borderRadius: 2, display: 'block' }}
            />
          </Box>
        </Box>

        <AnimatePresence>
          {menuOpen && (
            <Box
              component={motion.div}
              variants={mobileMenuVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              sx={{
                mt: 2,
                overflow: 'hidden',
                borderRadius: '24px',
                bgcolor: 'rgba(43,43,43,0.55)',
                border: '1px solid rgba(217,214,207,0.15)',
                backdropFilter: 'blur(16px) saturate(160%)',
                WebkitBackdropFilter: 'blur(16px) saturate(160%)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                display: { xs: 'block', md: 'none' },
              }}
            >
              <Stack sx={{ py: 2, px: 3 }} spacing={1}>
                {navLinks.map(({ key, label, icon: Icon }) => (
                  <Box component={motion.div} variants={mobileItemVariants} key={key}>
                    <Button
                      fullWidth
                      startIcon={<Icon size={18} />}
                      onClick={() => {
                        handleNavClick(key)
                        setMenuOpen(false)
                      }}
                      sx={{
                        justifyContent: 'flex-start',
                        color: 'rgba(217,214,207,0.85)',
                        fontSize: '1rem',
                        py: 1.2,
                        gap: 1,
                        '&:hover': { color: '#D9D6CF', bgcolor: 'rgba(217,214,207,0.06)' },
                      }}
                    >
                      {label}
                    </Button>
                  </Box>
                ))}

                <Box component={motion.div} variants={mobileItemVariants} sx={{ pt: 1 }}>
                  <LanguageSelect i18n={i18n} fullWidth />
                </Box>

                <Box component={motion.div} variants={mobileItemVariants} sx={{ pt: 1 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      handleNavClick('contact')
                      setMenuOpen(false)
                    }}
                    sx={{
                      bgcolor: '#7A2E3A',
                      color: '#D9D6CF',
                      py: 1.2,
                      borderRadius: '999px',
                      '&:hover': { bgcolor: '#8f3646' },
                    }}
                  >
                    {t('nav.cta')}
                  </Button>
                </Box>
              </Stack>
            </Box>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  )
}